import { VerticalProductCard } from "@shared/ui.components";
import type {
  MultipleProductCard,
  SingularProductCard,
  MultipleProductCardVariant,
  VariantType
} from "@shared/graphql.types";

import { ShadeSelectorDrawer } from "./ShadeSelectorDrawer";
import { useProductCard } from "./hooks/useProductCard";
import { isMultipleProductCard } from "packages/core-shopping/shared/isMultipleProductCard";
import type { ProductCardTrackingPayload } from "./types";

interface ProductCardWrapperProps {
  productCard: SingularProductCard | MultipleProductCard;
  location: string;
  enableShadeSelector?: boolean;
  enableButton?: boolean;
  styles?: {
    variant?: "standard" | "carousel";
    buttonVariant?: "primary" | "secondary";
  };
  productClickedTrackingPayload?: ProductCardTrackingPayload;
  addToBasketOptions?: {
    afterAdd?: (variant: VariantType) => void;
    beforeAdd?: () => void;
  };
  overrideProductCardClick?: () => void;
}

export const ProductCardWrapper = ({
  productCard,
  location,
  productClickedTrackingPayload,
  styles: { variant, buttonVariant } = {},
  enableShadeSelector = true,
  enableButton = true,
  addToBasketOptions,
  overrideProductCardClick
}: ProductCardWrapperProps) => {
  const { productCardData, activeVariant, isShadeSelectorOpen, handlers } =
    useProductCard(productCard, {
      location,
      addToBasketOptions,
      productClickedTrackingPayload
    });

  const handleShadeSelectorClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    event.nativeEvent.stopImmediatePropagation();

    if (enableShadeSelector && isMultipleProductCard(productCard)) {
      handlers.openShadeSelector();
    }
  };

  const shouldRenderShadeSelector =
    enableShadeSelector && isMultipleProductCard(productCard);

  return (
    <>
      {shouldRenderShadeSelector && (
        <ShadeSelectorDrawer
          product={productCard as MultipleProductCard}
          activeVariant={activeVariant as MultipleProductCardVariant}
          isOpen={isShadeSelectorOpen}
          onClose={handlers.closeShadeSelector}
          onShadeSelect={handlers.selectShade}
          resultId={productClickedTrackingPayload?.resultId}
          location={location}
          onATB={handlers.addToBasket}
        />
      )}

      <VerticalProductCard
        variant={variant ?? "standard"}
        buttonVariant={buttonVariant ?? "primary"}
        product={productCardData}
        onCardClick={overrideProductCardClick ?? handlers.trackProductClick}
        onButtonClick={enableButton ? handlers.addToBasket : undefined}
        onShadeSelectorClick={
          enableShadeSelector ? handleShadeSelectorClick : undefined
        }
        onWishlistToggle={handlers.toggleWishlist}
      />
    </>
  );
};
